import { AUTH_CONFIG } from 'app/modules/session/auth0Config';
import { LocalStorageKeys } from 'app/shared/constants/localStorage';
import { merge, cloneDeep } from 'lodash';

import { store } from 'app/store/configureStore';
// Utils
import { consoleError, consoleWarn } from 'app/shared/utils/console';
import {
  getImpersonateId,
  getOrgSwitcherAgentId,
} from 'app/shared/utils/sessionStorage';
import { v4 } from 'uuid';

// Actions
import { sendToast, sendErrorToast } from 'app/shared/toasts/actions';

// Permission auditor
import { addPermission as addPermissions } from 'app/modules/devtools/sliceDevtools';

const PERMISSION_HEADER_NAME = 'u21-required-permissions';

export const UPLOAD_ABORTED_MESSAGE = 'Upload aborted';

const defaultOptions = {
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
};

const ORG_ID_PATTERN = /"org_id"\s*:\s*(-?\d+)/g;
const EMPTY_AGENT_ORG_ID = new Set([-1, 0]);

const EXCLUDED_ENDPOINTS: RegExp[] = [
  ...['/database-connections/pull-based-datafiles/preview/:fileId'].map(
    toEndpointPattern,
  ),
  /^\/orgs\/\d+/,
  /^\/admin_dashboard\/org/,
];

let orgSwitchPreviousOrgId: number | null = null;

export function setPreSwitchOrgId(prevOrgId: number): void {
  orgSwitchPreviousOrgId = prevOrgId;
}

function isOrgSwitching(sessionOrgId: number): boolean {
  if (orgSwitchPreviousOrgId === null) return false;
  if (sessionOrgId !== orgSwitchPreviousOrgId) {
    orgSwitchPreviousOrgId = null;
    return false;
  }
  return true;
}

function toEndpointPattern(pattern: string): RegExp {
  const escaped = pattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&');
  const withWildcards = escaped.replace(/:\w+/g, '[^/]+');
  return new RegExp(`^${withWildcards}($|[?#])`);
}

export function findMismatchedOrgId(
  rawJson: string,
  expectedOrgId: number,
  allowedOrgId?: Set<number>,
): number | undefined {
  for (const match of rawJson.matchAll(ORG_ID_PATTERN)) {
    const orgId = parseInt(match[1], 10);

    if (
      !allowedOrgId?.has(orgId) &&
      !EMPTY_AGENT_ORG_ID.has(orgId) &&
      orgId !== expectedOrgId
    ) {
      return orgId;
    }
  }
  return undefined;
}

export function checkOrgIdInResponse(
  rawJson: string,
  endpoint: string,
  allowedOrgId?: Set<number>,
): void {
  const state = store.getState();
  const sessionOrgId = state.session?.agent?.org_id ?? -1;

  if (sessionOrgId === -1) {
    return;
  }

  if (isOrgSwitching(sessionOrgId)) {
    return;
  }

  const impersonateId = getImpersonateId();
  const orgSwitcherId = getOrgSwitcherAgentId();
  if (
    (impersonateId && impersonateId >= 0) ||
    (orgSwitcherId && orgSwitcherId >= 0)
  ) {
    return;
  }

  // Parents orgs have access to data from child org
  if (state.session?.relevantOrgs?.org_type === 'parent_org') {
    return;
  }

  if (EXCLUDED_ENDPOINTS.some((pattern) => pattern.test(endpoint))) {
    return;
  }

  const mismatchedOrgId = findMismatchedOrgId(
    rawJson,
    sessionOrgId,
    allowedOrgId,
  );
  if (mismatchedOrgId !== undefined) {
    consoleError(
      `Org ID mismatch detected in response from ${endpoint}: expected ${sessionOrgId}, found ${mismatchedOrgId}`,
    );
  }
}

function sendErrorToastIfMissingPermissions(response: Response) {
  try {
    const requiredPermissions = JSON.parse(
      response.headers.get('u21-required-permissions') || '[]',
    );

    if (response.status === 403 && requiredPermissions.length > 0) {
      const regex = /[:_]/gi;
      const formattedPermissions = requiredPermissions.map((permission) =>
        permission.replaceAll(regex, ' '),
      );

      store.dispatch(
        sendErrorToast(
          `Missing required permissions. Please ask an administrator to ensure you have all of the following permissions: ${formattedPermissions.join(
            ', ',
          )}.`,
        ),
      );

      consoleError(
        `${response.url.replace(window.location.origin, '')} missing required permissions`,
        { permissions: requiredPermissions },
      );
    }
  } catch {
    // Ignore exception
  }
}

function checkStatus(response: Response): Promise<Response | null> {
  // 204 is no content. return null to trigger the the next 'then' condition

  if (response.status === 204) {
    return Promise.resolve(null);
  }

  if (response.status >= 200 && response.status < 300) {
    return Promise.resolve(response);
  }

  consoleWarn(
    `HTTP ${response.status} accessing "${response.url}". See the full Response object below:`,
  );

  sendErrorToastIfMissingPermissions(response);

  return Promise.reject(response);
}

function generateQueryParamString(
  queryParams: { key: string; value: string }[],
): string {
  const resArr: string[] = [];
  queryParams.forEach(({ key, value }) => resArr.push(`${key}=${value}`));

  const res = resArr.join('&');
  return res ? `?${res}` : '';
}

function sendPermissionsToAuditor(header: string | null) {
  if (
    header === null ||
    window.sessionStorage.getItem('permissionsAuditor') !== 'true'
  ) {
    return;
  }
  const requiredPermissions = JSON.parse(header);
  store.dispatch(addPermissions(requiredPermissions));
}

export const fetchrBase = async (
  endpoint: string,
  queryParams: { key: string; value: string }[] = [],
  customOptions = defaultOptions,
  overwriteBaseUrl = false,
) => {
  const accessToken = localStorage.getItem(LocalStorageKeys.ACCESS_TOKEN);
  const options: any = cloneDeep(customOptions);
  options.headers.Authorization = `Bearer ${accessToken}`;
  options.headers.RequestID = v4();

  // Allow root users (i.e. u21-internal) to specify a user id to impersonate as
  const orgSwitcherId: number | null = getOrgSwitcherAgentId();
  const impersonateId: number | null = getImpersonateId();
  if (impersonateId && impersonateId >= 0) {
    options.headers['impersonate-as'] = impersonateId;
  } else if (orgSwitcherId && orgSwitcherId >= 0) {
    options.headers['org-switch-as'] = orgSwitcherId;
  }
  const base = overwriteBaseUrl ? '' : AUTH_CONFIG.apiBaseUrl;
  return fetch(
    `${base}${endpoint}${generateQueryParamString(queryParams)}`,
    options,
  ).then(checkStatus);
};

const fetchr = async <ResponseType = unknown>(
  endpoint: string,
  queryParams: { key: string; value: string }[] = [],
  customOptions = defaultOptions,
  allowedOrgId?: Set<number>,
  json = true,
  overwriteBaseUrl = false,
): Promise<ResponseType> => {
  return fetchrBase(
    endpoint,
    queryParams,
    customOptions,
    overwriteBaseUrl,
  ).then((res) => {
    if (res) {
      sendPermissionsToAuditor(res.headers.get(PERMISSION_HEADER_NAME));
      if (json) {
        res
          .clone()
          .text()
          .then((text) => checkOrgIdInResponse(text, endpoint, allowedOrgId))
          .catch(() => {});
        return res.json();
      }
      return res.text();
    }
    return {};
  }) as ResponseType;
};

export const getFileDownloadResponse = async (
  endpoint: string,
  method: string = 'GET',
  body: JSONObject = {},
): Promise<Response> => {
  const accessToken = localStorage.getItem(LocalStorageKeys.ACCESS_TOKEN);
  const options: any = cloneDeep(defaultOptions);
  options.method = method;
  options.headers.Authorization = `Bearer ${accessToken}`;
  options.headers.RequestID = v4();

  if (method !== 'GET') {
    options.body = JSON.stringify(body);
  }

  // Allow root users (i.e. u21-internal) to specify a user id to impersonate as
  const impersonateId: number | null = getImpersonateId();
  const orgSwitcherId: number | null = getOrgSwitcherAgentId();
  if (impersonateId && impersonateId >= 0) {
    options.headers['impersonate-as'] = impersonateId;
  } else if (orgSwitcherId && orgSwitcherId >= 0) {
    options.headers['org-switch-as'] = orgSwitcherId;
  }

  return fetch(`${AUTH_CONFIG.apiBaseUrl}${endpoint}`, options);
};

export const downloadFile = async (
  endpoint: string,
  fileName: string,
  method: string = 'GET',
  body: JSONObject = {},
) => {
  const accessToken = localStorage.getItem(LocalStorageKeys.ACCESS_TOKEN);
  const options: any = cloneDeep(defaultOptions);
  options.method = method;
  options.headers.Authorization = `Bearer ${accessToken}`;
  options.headers.RequestID = v4();

  if (method !== 'GET') {
    options.body = JSON.stringify(body);
  }

  // Allow root users (i.e. u21-internal) to specify a user id to impersonate as
  const impersonateId: number | null = getImpersonateId();
  const orgSwitcherId: number | null = getOrgSwitcherAgentId();
  if (impersonateId && impersonateId >= 0) {
    options.headers['impersonate-as'] = impersonateId;
  } else if (orgSwitcherId && orgSwitcherId >= 0) {
    options.headers['org-switch-as'] = orgSwitcherId;
  }

  return fetch(`${AUTH_CONFIG.apiBaseUrl}${endpoint}`, options)
    .then(checkStatus)
    .then((res: any) => {
      if (res) {
        sendPermissionsToAuditor(res.headers.get(PERMISSION_HEADER_NAME));
        res
          .blob()
          .then((blob) => {
            const url = window.URL.createObjectURL(new Blob([blob]));
            const a = document.createElement('a');
            document.body.appendChild(a);
            a.style.display = 'none';
            a.href = url;
            a.download = fileName;
            a.click();
            window.URL.revokeObjectURL(url);
          })
          .catch((error) => {
            sendToast({
              message: error,
              position: 'Top',
              type: 'Error',
            });
            throw new Error(`Error occurred:${error}`);
          });
      }
      return {};
    });
};

export function get<ResponseType = unknown>(
  endpoint: string,
  queryParams: { key: string; value: string }[] = [],
  config = {},
  allowedOrgId?: Set<number>,
) {
  const options: any = merge(cloneDeep(defaultOptions), { ...config });
  options.method = 'GET';

  return fetchr<ResponseType>(endpoint, queryParams, options, allowedOrgId);
}

export const getWithParsedError = async <T>(url: string): Promise<T> => {
  try {
    return await get<T>(url);
  } catch (e: unknown) {
    if (e instanceof Response) {
      const body = await e.json().catch(() => ({}));
      throw { status: e.status, ...body };
    }
    throw e;
  }
};

export function post<ResponseType = unknown>(
  endpoint: string,
  body = {},
  queryParams: { key: string; value: string }[] = [],
  config = {},
  allowedOrgId?: Set<number>,
) {
  const options: any = merge(cloneDeep(defaultOptions), { ...config });
  options.method = 'POST';
  options.body = JSON.stringify(body);

  return fetchr<ResponseType>(endpoint, queryParams, options, allowedOrgId);
}

export function put<ResponseType = unknown>(
  endpoint: string,
  body = {},
  config = {},
  allowedOrgId?: Set<number>,
) {
  const options: any = merge(cloneDeep(defaultOptions), { ...config });
  options.method = 'PUT';
  options.body = JSON.stringify(body);

  return fetchr<ResponseType>(endpoint, [], options, allowedOrgId);
}

export function patch<ResponseType = unknown>(
  endpoint: string,
  body = {},
  config = {},
  allowedOrgId?: Set<number>,
) {
  const options: any = merge(cloneDeep(defaultOptions), { ...config });
  options.method = 'PATCH';
  options.body = JSON.stringify(body);

  return fetchr<ResponseType>(endpoint, [], options, allowedOrgId);
}

export function destroy<ResponseType = unknown>(
  endpoint: string,
  config = {},
  allowedOrgId?: Set<number>,
) {
  const options: any = merge(cloneDeep(defaultOptions), { ...config });
  options.method = 'DELETE';

  return fetchr<ResponseType>(endpoint, [], options, allowedOrgId);
}

const getUploadHeaders = async () => {
  const accessToken = localStorage.getItem(LocalStorageKeys.ACCESS_TOKEN);

  const headers = {
    Authorization: `Bearer ${accessToken}`,
    RequestID: v4(),
  };

  // Allow root users (i.e. u21-internal) to specify a user id to impersonate as
  const impersonateId: number | null = getImpersonateId();
  const orgSwitcherId: number | null = getOrgSwitcherAgentId();
  if (impersonateId && impersonateId >= 0) {
    headers['impersonate-as'] = impersonateId;
  } else if (orgSwitcherId && orgSwitcherId >= 0) {
    headers['org-switch-as'] = orgSwitcherId;
  }

  return headers;
};

export const upload = async <ResponseType = unknown>(
  endpoint: string,
  body: FormData,
) => {
  const headers = await getUploadHeaders();

  return fetch(`${AUTH_CONFIG.apiBaseUrl}${endpoint}`, {
    method: 'POST',
    headers,
    body,
  }).then((response) => {
    sendPermissionsToAuditor(response.headers.get(PERMISSION_HEADER_NAME));
    if (response.status !== 200) {
      return Promise.reject(response);
    }
    return response.json();
  }) as Promise<ResponseType>;
};

export const postFormData = async <T = unknown>(
  endpoint: string,
  body: FormData,
): Promise<T> => {
  const headers = await getUploadHeaders();

  const response = await fetch(`${AUTH_CONFIG.apiBaseUrl}${endpoint}`, {
    method: 'POST',
    headers,
    body,
  });
  sendPermissionsToAuditor(response.headers.get(PERMISSION_HEADER_NAME));
  if (response.status < 200 || response.status >= 300) {
    return Promise.reject(response);
  }
  const text = await response.text();
  return (text ? JSON.parse(text) : {}) as T;
};

export const uploadWithProgress = (endpoint: string, body: FormData) => {
  // This function cannot be async because we want to return the XHR object immediately

  const req = new XMLHttpRequest();
  const uploadPromise = new Promise<any>((resolve, reject) => {
    req.open('POST', `${AUTH_CONFIG.apiBaseUrl}${endpoint}`);
    req.withCredentials = true;

    req.addEventListener('readystatechange', () => {
      if (req.readyState !== XMLHttpRequest.DONE) {
        return;
      }

      // If there's no status,
      // it means we never got a response from the server
      if (req.status === 0) {
        reject(new Error(UPLOAD_ABORTED_MESSAGE));
      } else if (req.status !== 200) {
        sendPermissionsToAuditor(req.getResponseHeader(PERMISSION_HEADER_NAME));
        let response = 'Could not parse the response';
        try {
          response = JSON.parse(req.responseText);
        } catch {}

        reject(response);
      } else {
        resolve(JSON.parse(req.responseText));
      }
    });

    // Since this function cannot be async
    // we use .then() here to add the headers before we send off the request
    getUploadHeaders().then((headers) => {
      for (const [key, value] of Object.entries(headers)) {
        req.setRequestHeader(key, value);
      }
      req.send(body);
    });
  });

  return { req, uploadPromise };
};
